import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { api } from '../../lib/api';
import {
  ArrowLeft,
  User,
  Monitor,
  Clock,
  AlertTriangle,
  CheckCircle2,
  Calendar,
  Building,
  Mail,
  Shield,
  Activity,
  WifiOff,
} from 'lucide-react';

interface UserProfileData {
  user: {
    id: string;
    fullName: string;
    email: string;
    department: string | null;
    role: string;
    disabled: boolean;
    createdAt: string;
  };
  device: {
    id: string;
    deviceId: string;
    hostname: string;
    deviceName: string;
    status: string;
    os: string;
    agentVersion: string;
    lastSeenAt: string;
    lastActivityAt: string | null;
  } | null;
  presence: {
    status: string;
    activeInactivity: {
      id: string;
      startedAt: string;
      elapsedSeconds: number;
    } | null;
  };
  todayMetrics: {
    totalInactiveSeconds: number;
    eventCount: number;
    longestInactiveSeconds: number;
  };
  timeline: Array<{
    id: string;
    startedAt: string;
    endedAt: string | null;
    durationSeconds: number;
    thresholdSeconds: number;
    status: string;
    device?: { hostname: string; deviceName: string };
  }>;
}

export function UserDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [profile, setProfile] = useState<UserProfileData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    const timer = setInterval(() => {
      setNow(Date.now());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const fetchProfile = async () => {
    if (!id) return;
    try {
      setLoading(true);
      setError(null);
      const res = await api.admin.getUserProfile(id);
      setProfile(res as UserProfileData);
    } catch (err: any) {
      setError(err.message || 'Failed to load user profile');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProfile();
  }, [id]);

  const formatSeconds = (totalSecs: number) => {
    if (!totalSecs || totalSecs <= 0) return '0s';
    const hrs = Math.floor(totalSecs / 3600);
    const mins = Math.floor((totalSecs % 3600) / 60);
    const secs = totalSecs % 60;
    if (hrs > 0) return `${hrs}h ${mins}m ${secs}s`;
    if (mins > 0) return `${mins}m ${secs}s`;
    return `${secs}s`;
  };

  const getElapsedLive = (startedAt: string) => {
    const elapsedSecs = Math.max(0, Math.floor((now - new Date(startedAt).getTime()) / 1000));
    const mins = Math.floor(elapsedSecs / 60);
    const secs = elapsedSecs % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <div className="py-20 flex flex-col items-center justify-center gap-3">
        <div className="w-6 h-6 border-2 border-pulse-blue border-t-transparent rounded-full animate-spin" />
        <span className="text-xs text-zinc-500 font-mono">Loading user profile & workstation telemetry...</span>
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div className="space-y-4">
        <Link
          to="/admin/users"
          className="inline-flex items-center gap-1 text-xs text-zinc-400 hover:text-white transition"
        >
          <ArrowLeft className="w-3.5 h-3.5" />
          <span>Back to Users</span>
        </Link>
        <div className="p-4 bg-rose-500/10 border border-rose-500/20 rounded-xl text-xs text-rose-400">
          {error || 'User not found'}
        </div>
      </div>
    );
  }

  const { user, device, presence, todayMetrics, timeline } = profile;
  const isInactive = presence.status === 'INACTIVE';
  const isActive = presence.status === 'ACTIVE';
  const isOffline = presence.status === 'OFFLINE';

  return (
    <div className="p-3 sm:p-6 lg:p-8 space-y-4 sm:space-y-6 max-w-7xl mx-auto w-full min-w-0">
      {/* Top Bar with Back Button */}
      <div className="flex items-center justify-between">
        <Link
          to="/admin/users"
          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-xl bg-white dark:bg-zinc-900/80 hover:bg-zinc-100 dark:hover:bg-zinc-800 text-xs font-semibold text-zinc-800 dark:text-zinc-300 border border-zinc-200 dark:border-zinc-800 transition shadow-sm active:scale-95"
        >
          <ArrowLeft className="w-3.5 h-3.5 text-zinc-500" />
          <span>Back to Users</span>
        </Link>

        {/* Presence Badge */}
        {isActive && (
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20">
            <span className="w-2 h-2 rounded-full bg-emerald-500 dark:bg-emerald-400 animate-pulse" />
            <span>Currently Active</span>
          </div>
        )}
        {isInactive && (
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold bg-amber-500/15 text-amber-600 dark:text-amber-400 border border-amber-500/30 animate-pulse">
            <AlertTriangle className="w-3.5 h-3.5" />
            <span>
              Inactive for{' '}
              {presence.activeInactivity ? getElapsedLive(presence.activeInactivity.startedAt) : '00:00'}
            </span>
          </div>
        )}
        {isOffline && (
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium bg-zinc-100 dark:bg-zinc-800/80 text-zinc-600 dark:text-zinc-400 border border-zinc-200 dark:border-zinc-700">
            <span className="w-2 h-2 rounded-full bg-zinc-400" />
            <span>Workstation Offline</span>
          </div>
        )}
      </div>

      {/* User and Device Hero Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* User Card */}
        <div className="p-5 rounded-2xl bg-white/80 dark:bg-zinc-900/60 border border-zinc-200/80 dark:border-zinc-800/80 space-y-4 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-blue-500/10 border border-blue-500/20 flex items-center justify-center text-pulse-blue font-bold text-lg">
              {user.fullName.charAt(0).toUpperCase()}
            </div>
            <div>
              <h2 className="text-base font-semibold text-zinc-900 dark:text-white">{user.fullName}</h2>
              <div className="text-xs text-zinc-500 dark:text-zinc-400 flex items-center gap-1.5 mt-0.5">
                <Mail className="w-3 h-3 text-zinc-400" />
                <span>{user.email}</span>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2 text-xs pt-2 border-t border-zinc-100 dark:border-zinc-800">
            <div>
              <span className="text-zinc-500 block text-[11px]">Department</span>
              <span className="text-zinc-800 dark:text-zinc-200 font-medium">{user.department || 'General'}</span>
            </div>
            <div>
              <span className="text-zinc-500 block text-[11px]">Role</span>
              <span className="text-zinc-800 dark:text-zinc-200 font-medium">{user.role}</span>
            </div>
            <div className="mt-2">
              <span className="text-zinc-500 block text-[11px]">Account Status</span>
              <span
                className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold mt-0.5 ${
                  user.disabled
                    ? 'bg-rose-500/10 text-rose-500 dark:text-rose-400 border border-rose-500/20'
                    : 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20'
                }`}
              >
                {user.disabled ? 'Disabled' : 'Active'}
              </span>
            </div>
            <div className="mt-2">
              <span className="text-zinc-500 block text-[11px]">Enrolled On</span>
              <span className="text-zinc-800 dark:text-zinc-200 font-medium">
                {new Date(user.createdAt).toLocaleDateString()}
              </span>
            </div>
          </div>
        </div>

        {/* Workstation Card */}
        <div className="p-5 rounded-2xl bg-white/80 dark:bg-zinc-900/60 border border-zinc-200/80 dark:border-zinc-800/80 space-y-4 shadow-sm">
          <div className="flex items-center gap-3">
            <div
              className={`w-12 h-12 rounded-2xl flex items-center justify-center border ${
                device
                  ? 'bg-zinc-100 dark:bg-zinc-800/80 border-zinc-200 dark:border-zinc-700 text-zinc-700 dark:text-zinc-200'
                  : 'bg-zinc-50 dark:bg-zinc-900 border-zinc-200 dark:border-zinc-800 text-zinc-400 dark:text-zinc-600'
              }`}
            >
              <Monitor className="w-6 h-6" />
            </div>
            <div>
              <h3 className="text-base font-semibold text-zinc-900 dark:text-white">
                {device ? device.hostname : 'No Paired Workstation'}
              </h3>
              <div className="text-xs text-zinc-500 dark:text-zinc-400 font-mono mt-0.5">
                {device ? `${device.deviceId} • v${device.agentVersion}` : 'Agent pairing required'}
              </div>
            </div>
          </div>

          {device ? (
            <div className="grid grid-cols-2 gap-2 text-xs pt-2 border-t border-zinc-100 dark:border-zinc-800">
              <div>
                <span className="text-zinc-500 block text-[11px]">Operating System</span>
                <span className="text-zinc-800 dark:text-zinc-200 font-medium">{device.os}</span>
              </div>
              <div>
                <span className="text-zinc-500 block text-[11px]">Presence</span>
                <span className="text-zinc-800 dark:text-zinc-200 font-medium">{device.status}</span>
              </div>
              <div className="mt-2">
                <span className="text-zinc-500 block text-[11px]">Last Activity</span>
                <span className="text-zinc-800 dark:text-zinc-200 font-medium">
                  {device.lastActivityAt
                    ? new Date(device.lastActivityAt).toLocaleTimeString()
                    : 'N/A'}
                </span>
              </div>
              <div className="mt-2">
                <span className="text-zinc-500 block text-[11px]">Last Heartbeat</span>
                <span className="text-zinc-800 dark:text-zinc-200 font-medium">
                  {new Date(device.lastSeenAt).toLocaleTimeString()}
                </span>
              </div>
            </div>
          ) : (
            <div className="text-xs text-zinc-500 pt-2 border-t border-zinc-100 dark:border-zinc-800">
              Generate an enrollment pairing token on the Users page to connect this employee's Windows workstation.
            </div>
          )}
        </div>
      </div>

      {/* Today's KPI Metrics */}
      <div>
        <h3 className="text-sm font-semibold text-zinc-800 dark:text-zinc-300 mb-3">Today's Inactivity Metrics</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="p-4 rounded-2xl bg-white/80 dark:bg-zinc-900/60 border border-zinc-200/80 dark:border-zinc-800/80 shadow-sm">
            <div className="text-[11px] font-medium text-zinc-500 dark:text-zinc-400 uppercase tracking-wider">
              Total Inactive Today
            </div>
            <div className="text-2xl font-bold text-amber-500 dark:text-amber-300 mt-1">
              {formatSeconds(todayMetrics.totalInactiveSeconds)}
            </div>
            <div className="text-[11px] text-zinc-500 mt-1 flex items-center gap-1">
              <Clock className="w-3 h-3 text-amber-500" />
              <span>Cumulative idle duration</span>
            </div>
          </div>

          <div className="p-4 rounded-2xl bg-white/80 dark:bg-zinc-900/60 border border-zinc-200/80 dark:border-zinc-800/80 shadow-sm">
            <div className="text-[11px] font-medium text-zinc-500 dark:text-zinc-400 uppercase tracking-wider">
              Incidents Today
            </div>
            <div className="text-2xl font-bold text-zinc-900 dark:text-white mt-1">{todayMetrics.eventCount}</div>
            <div className="text-[11px] text-zinc-500 mt-1 flex items-center gap-1">
              <AlertTriangle className="w-3 h-3 text-amber-500" />
              <span>Inactivity threshold alerts</span>
            </div>
          </div>

          <div className="p-4 rounded-2xl bg-white/80 dark:bg-zinc-900/60 border border-zinc-200/80 dark:border-zinc-800/80 shadow-sm">
            <div className="text-[11px] font-medium text-zinc-500 dark:text-zinc-400 uppercase tracking-wider">
              Peak Inactive Period
            </div>
            <div className="text-2xl font-bold text-zinc-800 dark:text-zinc-200 mt-1">
              {formatSeconds(todayMetrics.longestInactiveSeconds)}
            </div>
            <div className="text-[11px] text-zinc-500 mt-1 flex items-center gap-1">
              <Activity className="w-3 h-3 text-blue-500" />
              <span>Longest continuous idle session</span>
            </div>
          </div>
        </div>
      </div>

      {/* Timeline of Today's Inactivity Events */}
      <div className="space-y-3">
        <h3 className="text-sm font-semibold text-zinc-800 dark:text-zinc-300">Today's Activity Timeline</h3>

        <div className="rounded-2xl border border-zinc-200/80 dark:border-[#222222] bg-white/90 dark:bg-[#0E0E0E] overflow-hidden shadow-sm dark:shadow-2xl p-4">
          {timeline.length === 0 ? (
            <div className="py-8 text-center text-zinc-500 text-xs">
              No inactivity events recorded for {user.fullName} today.
            </div>
          ) : (
            <div className="space-y-3">
              {timeline.map((ev) => {
                const isOngoing = ev.status === 'ACTIVE';

                return (
                  <div
                    key={ev.id}
                    className="flex items-start gap-3 p-3.5 rounded-xl bg-zinc-50 dark:bg-zinc-900/40 border border-zinc-200/80 dark:border-zinc-800/60"
                  >
                    <div
                      className={`w-8 h-8 rounded-xl flex items-center justify-center border mt-0.5 ${
                        isOngoing
                          ? 'bg-amber-500/10 border-amber-500/30 text-amber-500 dark:text-amber-400'
                          : 'bg-emerald-500/10 border-emerald-500/30 text-emerald-600 dark:text-emerald-400'
                      }`}
                    >
                      {isOngoing ? (
                        <AlertTriangle className="w-4 h-4" />
                      ) : (
                        <CheckCircle2 className="w-4 h-4" />
                      )}
                    </div>

                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <div className="font-semibold text-xs text-zinc-900 dark:text-white">
                          {isOngoing ? 'Active Inactivity Incident' : 'Resolved Inactivity Event'}
                        </div>
                        <span className="font-mono text-xs font-semibold text-amber-600 dark:text-amber-400">
                          {formatSeconds(ev.durationSeconds)} ({ev.durationSeconds}s)
                        </span>
                      </div>

                      <div className="text-[11px] text-zinc-500 dark:text-zinc-400 mt-1 flex flex-wrap items-center gap-3">
                        <div>
                          <span className="text-zinc-400">Started: </span>
                          <span className="font-mono text-zinc-700 dark:text-zinc-300">
                            {new Date(ev.startedAt).toLocaleTimeString()}
                          </span>
                        </div>
                        <div>
                          <span className="text-zinc-400">Ended: </span>
                          <span className="font-mono text-zinc-700 dark:text-zinc-300">
                            {ev.endedAt ? new Date(ev.endedAt).toLocaleTimeString() : 'Ongoing'}
                          </span>
                        </div>
                        {ev.device && (
                          <div className="text-zinc-500">
                            Workstation: {ev.device.hostname}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
